import { useState } from 'react';
import { X, Cloud, Loader, CheckCircle, AlertCircle, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { awsService } from '../../services/awsService';

const CLOUDFORMATION_TEMPLATE = `AWSTemplateFormatVersion: '2010-09-09'
Description: 'IAM Role for Quantifaid AWS Integration'

Resources:
  QuantifaidCollectorRole:
    Type: AWS::IAM::Role
    Properties:
      RoleName: QuantifaidCollector
      AssumeRolePolicyDocument:
        Version: '2012-10-17'
        Statement:
          - Effect: Allow
            Principal:
              AWS: arn:aws:iam::395709833816:root
            Action: sts:AssumeRole
            Condition:
              StringEquals:
                sts:ExternalId: qfid-42
      ManagedPolicyArns:
        - arn:aws:iam::aws:policy/CloudWatchReadOnlyAccess
        - arn:aws:iam::aws:policy/AWSLambda_ReadOnlyAccess
        - arn:aws:iam::aws:policy/AmazonSageMakerReadOnly
        - arn:aws:iam::aws:policy/AmazonBedrockReadOnly
        - arn:aws:iam::aws:policy/AmazonRekognitionReadOnlyAccess
        - arn:aws:iam::aws:policy/AmazonComprehendReadOnly
        - arn:aws:iam::aws:policy/AmazonLexReadOnly
        - arn:aws:iam::aws:policy/AmazonPollyReadOnlyAccess
        - arn:aws:iam::aws:policy/AmazonTextractReadOnly
        - arn:aws:iam::aws:policy/AmazonTranscribeReadOnlyAccess
        - arn:aws:iam::aws:policy/AmazonTranslateReadOnly
`;

const TERRAFORM_TEMPLATE = `resource "aws_iam_role" "quantifaid_collector" {
  name = "QuantifaidCollector"
  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [
      {
        Effect = "Allow"
        Principal = {
          AWS = "arn:aws:iam::395709833816:root"
        }
        Action = "sts:AssumeRole"
        Condition = {
          StringEquals = {
            "sts:ExternalId" = "qfid-42"
          }
        }
      }
    ]
  })
}

resource "aws_iam_role_policy_attachment" "cloudwatch" {
  role       = aws_iam_role.quantifaid_collector.name
  policy_arn = "arn:aws:iam::aws:policy/CloudWatchReadOnlyAccess"
}

resource "aws_iam_role_policy_attachment" "lambda" {
  role       = aws_iam_role.quantifaid_collector.name
  policy_arn = "arn:aws:iam::aws:policy/AWSLambda_ReadOnlyAccess"
}

resource "aws_iam_role_policy_attachment" "sagemaker" {
  role       = aws_iam_role.quantifaid_collector.name
  policy_arn = "arn:aws:iam::aws:policy/AmazonSageMakerReadOnly"
}

resource "aws_iam_role_policy_attachment" "bedrock" {
  role       = aws_iam_role.quantifaid_collector.name
  policy_arn = "arn:aws:iam::aws:policy/AmazonBedrockReadOnly"
}

resource "aws_iam_role_policy_attachment" "rekognition" {
  role       = aws_iam_role.quantifaid_collector.name
  policy_arn = "arn:aws:iam::aws:policy/AmazonRekognitionReadOnlyAccess"
}

resource "aws_iam_role_policy_attachment" "comprehend" {
  role       = aws_iam_role.quantifaid_collector.name
  policy_arn = "arn:aws:iam::aws:policy/AmazonComprehendReadOnly"
}

resource "aws_iam_role_policy_attachment" "lex" {
  role       = aws_iam_role.quantifaid_collector.name
  policy_arn = "arn:aws:iam::aws:policy/AmazonLexReadOnly"
}

resource "aws_iam_role_policy_attachment" "polly" {
  role       = aws_iam_role.quantifaid_collector.name
  policy_arn = "arn:aws:iam::aws:policy/AmazonPollyReadOnlyAccess"
}

resource "aws_iam_role_policy_attachment" "textract" {
  role       = aws_iam_role.quantifaid_collector.name
  policy_arn = "arn:aws:iam::aws:policy/AmazonTextractReadOnly"
}

resource "aws_iam_role_policy_attachment" "transcribe" {
  role       = aws_iam_role.quantifaid_collector.name
  policy_arn = "arn:aws:iam::aws:policy/AmazonTranscribeReadOnlyAccess"
}

resource "aws_iam_role_policy_attachment" "translate" {
  role       = aws_iam_role.quantifaid_collector.name
  policy_arn = "arn:aws:iam::aws:policy/AmazonTranslateReadOnly"
}
`;

export default function AwsDrawer({ isOpen, onClose, onConnected }) {
  const [roleArn, setRoleArn] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showCloudFormation, setShowCloudFormation] = useState(false);
  const [showTerraform, setShowTerraform] = useState(false);
  const [copied, setCopied] = useState('');

  const handleConnect = async () => {
    try {
      setLoading(true);
      setError(null);
      await awsService.connectAWS(roleArn);
      setSuccess(true);
      onConnected();
    } catch (err) {
      setError(err.message || 'Failed to connect AWS account. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50">
      <div className="fixed inset-0" onClick={onClose} />
      
      <div className="absolute right-0 top-0 h-full w-[calc(100%-320px)] bg-background border-l border-primary/20">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Cloud size={24} className="text-primary" />
              <h2 className="text-xl font-semibold">Connect AWS</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-danger/10 text-danger rounded-lg text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {success ? (
            <div className="flex flex-col items-center justify-center py-12">
              <CheckCircle size={48} className="text-primary mb-4" />
              <h3 className="text-lg font-medium mb-2">AWS Connected Successfully</h3>
              <p className="text-text-secondary text-center mb-6">
                Your AWS account has been connected. You can now monitor AWS AI services and resources.
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-primary text-background rounded-lg hover:bg-primary-light transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2">AWS Role ARN</h3>
                  <p className="text-sm text-text-secondary mb-4">
                    Enter the ARN of the IAM role that Quantifaid will use to access your AWS resources.
                  </p>
                  <input
                    type="text"
                    value={roleArn}
                    onChange={(e) => setRoleArn(e.target.value)}
                    placeholder="Enter the role ARN from your AWS environment"
                    className="w-full bg-background-light border border-primary/20 rounded-lg px-4 py-2"
                  />
                </div>

                <div className="bg-background-light rounded-lg p-4">
                  <h4 className="font-medium mb-2">Required Permissions</h4>
                  <ul className="list-disc list-inside text-sm text-text-secondary space-y-1">
                    <li>Read access to CloudWatch logs</li>
                    <li>Read access to AWS AI services</li>
                    <li>Read access to resource configurations</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <div className="bg-background-light rounded-lg p-4">
                    <h4 className="font-medium mb-2">Setup Instructions</h4>
                    <ol className="list-decimal list-inside text-sm text-text-secondary space-y-2">
                      <li>Choose your preferred deployment method (CloudFormation or Terraform)</li>
                      <li>Deploy the template in your AWS environment</li>
                      <li>After deployment, copy the created role ARN</li>
                      <li>Paste the role ARN in the field above</li>
                      <li>Click "Connect AWS" to complete the integration</li>
                    </ol>
                  </div>

                  <button
                    onClick={() => setShowCloudFormation(!showCloudFormation)}
                    className="w-full flex items-center justify-between p-4 bg-background-light rounded-lg hover:bg-background-light/80 transition-colors"
                  >
                    <span className="font-medium">CloudFormation Template</span>
                    {showCloudFormation ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>

                  {showCloudFormation && (
                    <div className="relative bg-background-light rounded-lg p-4">
                      <div className="mb-4 text-sm text-text-secondary">
                        <p className="font-medium mb-2">Deployment Steps:</p>
                        <ol className="list-decimal list-inside space-y-1">
                          <li>Save this template as a YAML file (e.g., quantifaid-role.yaml)</li>
                          <li>Open the AWS CloudFormation console</li>
                          <li>Click "Create stack" and choose "With new resources"</li>
                          <li>Upload the template file</li>
                          <li>Click "Next" and follow the prompts</li>
                          <li>After deployment, find the role ARN in the Outputs tab</li>
                        </ol>
                      </div>
                      <button
                        onClick={() => handleCopy(CLOUDFORMATION_TEMPLATE, 'cloudformation')}
                        className="absolute top-2 right-2 p-1 hover:bg-primary/10 rounded-lg transition-colors"
                      >
                        <Copy size={16} />
                      </button>
                      <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
                        {CLOUDFORMATION_TEMPLATE}
                      </pre>
                      {copied === 'cloudformation' && (
                        <div className="absolute top-2 right-12 text-xs text-primary">
                          Copied!
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={() => setShowTerraform(!showTerraform)}
                    className="w-full flex items-center justify-between p-4 bg-background-light rounded-lg hover:bg-background-light/80 transition-colors"
                  >
                    <span className="font-medium">Terraform Template</span>
                    {showTerraform ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>

                  {showTerraform && (
                    <div className="relative bg-background-light rounded-lg p-4">
                      <div className="mb-4 text-sm text-text-secondary">
                        <p className="font-medium mb-2">Deployment Steps:</p>
                        <ol className="list-decimal list-inside space-y-1">
                          <li>Save this template as a .tf file (e.g., quantifaid-role.tf)</li>
                          <li>Initialize Terraform: <code className="bg-background px-1 rounded">terraform init</code></li>
                          <li>Review the plan: <code className="bg-background px-1 rounded">terraform plan</code></li>
                          <li>Apply the changes: <code className="bg-background px-1 rounded">terraform apply</code></li>
                          <li>After deployment, find the role ARN in the Terraform output</li>
                        </ol>
                      </div>
                      <button
                        onClick={() => handleCopy(TERRAFORM_TEMPLATE, 'terraform')}
                        className="absolute top-2 right-2 p-1 hover:bg-primary/10 rounded-lg transition-colors"
                      >
                        <Copy size={16} />
                      </button>
                      <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
                        {TERRAFORM_TEMPLATE}
                      </pre>
                      {copied === 'terraform' && (
                        <div className="absolute top-2 right-12 text-xs text-primary">
                          Copied!
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleConnect}
                  disabled={loading || !roleArn}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    loading || !roleArn
                      ? 'bg-primary/20 text-text-secondary cursor-not-allowed'
                      : 'bg-primary text-background hover:bg-primary-light'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <Loader size={16} className="animate-spin" />
                      Connecting...
                    </div>
                  ) : (
                    'Connect AWS'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 